import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import i18n from "i18n"

const SESSION_KEY = "guest_booking_access"

// Actions
const verifyRequested = createAction("guestAccess/verifyRequested")
const verifySuccess = createAction("guestAccess/verifySuccess")
const verifyFailed = createAction("guestAccess/verifyFailed")
const cleared = createAction("guestAccess/cleared")

// Which identity-verification method applies to this booking's client:
// "email" (default, existing flow) or "phone" (client has no real email on file).
const methodRequested = createAction("guestAccess/methodRequested")
const methodReceived = createAction("guestAccess/methodReceived")
const methodFailed = createAction("guestAccess/methodFailed")

// Phone flow, step 1: send an OTP by SMS to the phone number on file.
const otpSendRequested = createAction("guestAccess/otpSendRequested")
const otpSendSuccess = createAction("guestAccess/otpSendSuccess")
const otpSendFailed = createAction("guestAccess/otpSendFailed")

const getInitialState = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...LOADING_INITIAL_STATE,
        authorized: parsed.authorized || false,
        bookingUuid: parsed.bookingUuid || null,
        method: null,
        isMethodLoading: true,
        isOtpSent: false,
        phoneNumber: null,
        error: null
      }
    }
  } catch (e) {
    // ignore malformed session data
  }
  return {
    ...LOADING_INITIAL_STATE,
    authorized: false,
    bookingUuid: null,
    method: null,
    isMethodLoading: true,
    isOtpSent: false,
    phoneNumber: null,
    error: null
  }
}

const guestAccessReducer = createReducer(getInitialState(), {
  [methodRequested.type]: (state) => {
    setRequested(state)
    state.isMethodLoading = true
    state.error = null
    // Reset any state left over from a previous verification target so a
    // fresh method-determination call (e.g. a new bookingUuid) never leaks
    // the prior booking's OTP-sent/phone/method state into this one.
    state.isOtpSent = false
    state.phoneNumber = null
    state.method = null
  },
  [methodReceived.type]: (state, action) => {
    setReceived(state)
    state.isMethodLoading = false
    state.method = action.payload.method
  },
  [methodFailed.type]: (state, action) => {
    setFailed(state)
    state.isMethodLoading = false
    state.error =
      action.payload?.data?.message || action.payload?.message || i18n.t("guest_verify.booking_load_failed")
  },
  [otpSendRequested.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [otpSendSuccess.type]: (state, action) => {
    setReceived(state)
    state.isOtpSent = true
    state.phoneNumber = action.payload.phoneNumber
  },
  [otpSendFailed.type]: (state, action) => {
    setFailed(state)
    state.isOtpSent = false
    state.error =
      action.payload?.data?.message || action.payload?.message || i18n.t("guest_verify.phone_number_mismatch")
  },
  [verifyRequested.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [verifySuccess.type]: (state, action) => {
    setReceived(state)
    state.authorized = true
    state.bookingUuid = action.payload.bookingUuid
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ authorized: true, bookingUuid: action.payload.bookingUuid })
    )
  },
  [verifyFailed.type]: (state, action) => {
    setFailed(state)
    state.authorized = false
    state.error =
      action.payload?.data?.message ||
      action.payload?.message ||
      i18n.t("guest_verify.verification_failed")
  },
  [cleared.type]: () => {
    sessionStorage.removeItem(SESSION_KEY)
    localStorage.removeItem("guest_token")
    return {
      ...LOADING_INITIAL_STATE,
      authorized: false,
      bookingUuid: null,
      method: null,
      isMethodLoading: true,
      isOtpSent: false,
      phoneNumber: null,
      error: null
    }
  }
})

export default guestAccessReducer

// Action Creators

/**
 * Stores the guest access tokens returned by a successful verification call
 * and grants access, shared by both the email and phone (OTP) flows.
 */
const grantGuestAccess = (result, uuid, dispatch) => {
  if (result?.access_token) {
    localStorage.setItem("guest_token", result.access_token)
  }
  if (result?.refresh_token) {
    localStorage.setItem("refresh_token", result.refresh_token)
  }
  dispatch(verifySuccess({ bookingUuid: uuid }))
  // Dispatch custom event to notify components
  window.dispatchEvent(new Event("guestAuthSuccess"))
}

/**
 * Verifies that the provided email matches the primary guest for the booking.
 * On success, the backend returns a guest_token which is stored automatically
 * by the api helper, enabling subsequent authenticated API calls.
 */
export const verifyGuestEmail = (uuid, email) => async (dispatch) => {
  dispatch(verifyRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/authorize_client`,
        method: "get",
        params: { email },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    grantGuestAccess(result, uuid, dispatch)
    return result
  } catch (error) {
    dispatch(verifyFailed(error))
    throw error
  }
}

/**
 * Determines whether this booking's client should be verified by email or by
 * phone (clients imported/created without a real email are phone-only).
 */
export const getGuestVerificationMethod = (uuid) => async (dispatch) => {
  dispatch(methodRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/verification_method`,
        method: "get",
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    dispatch(methodReceived({ method: result?.method || "email" }))
    return result
  } catch (error) {
    dispatch(methodFailed(error))
    throw error
  }
}

/**
 * Phone flow, step 1: confirms the entered phone number matches the booking's
 * client and triggers an SMS OTP.
 */
export const sendGuestBookingOtp = (uuid, phoneNumber) => async (dispatch) => {
  dispatch(otpSendRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/send_booking_otp`,
        method: "post",
        data: { phone_number: phoneNumber },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    dispatch(otpSendSuccess({ phoneNumber }))
    return result
  } catch (error) {
    dispatch(otpSendFailed(error))
    throw error
  }
}

/**
 * Phone flow, step 2: verifies the SMS OTP and grants access, mirroring
 * verifyGuestEmail's token handling above.
 */
export const verifyGuestBookingOtp = (uuid, otp) => async (dispatch) => {
  dispatch(verifyRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/verify_booking_otp`,
        method: "post",
        data: { otp },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    grantGuestAccess(result, uuid, dispatch)
    return result
  } catch (error) {
    dispatch(verifyFailed(error))
    throw error
  }
}

export const clearGuestAccess = () => cleared()
